import React, { useState, useEffect, useRef } from "react";
import { useToast } from "../../contexts/ToastContext";
import api from "../../utils/api/axiosInstance";
import Modal from "../common/Modal";
import getImageUrl, { resolveProductImagePath } from "../../utils/imageUtils";
import MentionDropdown from "../common/MentionDropdown";
import { useMentionSearch } from "../../hooks/useMentionSearch";
import capitalizeFirst from "../../utils/capitalizeFirst";
import { MAX_IMAGE_UPLOAD_BYTES } from "../../constants/uploadLimits";
import {
  FaTimes,
  FaImage,
  FaSearch,
  FaTag,
  FaGlobe,
  FaUserFriends,
  FaLock,
  FaFire,
  FaBolt,
  FaQuestionCircle,
} from "react-icons/fa";
import styles from "../../styles/components/profile/CreatePostModal.module.css";

const ProductSuggestionThumb = ({ product }) => {
  const imagePath = resolveProductImagePath(product);
  if (imagePath) {
    return (
      <img
        src={getImageUrl(imagePath)}
        alt={product.name || "Product"}
        className={styles.suggestionImage}
      />
    );
  }
  return <div className={styles.suggestionImagePlaceholder} aria-hidden />;
};

const CreatePostModal = ({ isOpen, onClose, onPostCreated, initialPostType, initialProducts }) => {
  const { showToast } = useToast();
  const [step, setStep] = useState(1); // 1: Images, 2: Products, 3: Details

  // Form state
  const [postType, setPostType] = useState(initialPostType || "standard");
  const [productImages, setProductImages] = useState({});
  const [productImagePreviews, setProductImagePreviews] = useState({});
  const [generalImages, setGeneralImages] = useState([]);
  const [generalImagePreviews, setGeneralImagePreviews] = useState([]);
  const [products, setProducts] = useState(initialProducts || []);
  const [productSearch, setProductSearch] = useState("");
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [tags, setTags] = useState("");
  const [location, setLocation] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [comparisonOptionAProductId, setComparisonOptionAProductId] =
    useState("");
  const [comparisonOptionBProductId, setComparisonOptionBProductId] =
    useState("");
  const [comparisonOptionATitle, setComparisonOptionATitle] = useState("");
  const [comparisonOptionBTitle, setComparisonOptionBTitle] = useState("");
  const [battleDuration, setBattleDuration] = useState("24");

  // Per-side product search for Battle
  const [battleSideASearch, setBattleSideASearch] = useState("");
  const [battleSideBSearch, setBattleSideBSearch] = useState("");
  const [battleSideASuggestions, setBattleSideASuggestions] = useState([]);
  const [battleSideBSuggestions, setBattleSideBSuggestions] = useState([]);
  const [battleSideAShowSugg, setBattleSideAShowSugg] = useState(false);
  const [battleSideBShowSugg, setBattleSideBShowSugg] = useState(false);
  const [battleSideAProduct, setBattleSideAProduct] = useState(null);
  const [battleSideBProduct, setBattleSideBProduct] = useState(null);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  // Reset form search state when modal closes; restore defaults when it opens
  useEffect(() => {
    if (!isOpen) {
      setProductSearch("");
      setProductSuggestions([]);
      setShowSuggestions(false);
      setBattleSideASearch("");
      setBattleSideBSearch("");
      setBattleSideASuggestions([]);
      setBattleSideBSuggestions([]);
      setBattleSideAShowSugg(false);
      setBattleSideBShowSugg(false);
      return;
    }

    setPostType(initialPostType || "standard");
    setProducts(initialProducts || []);
  }, [isOpen, initialPostType, initialProducts]);

  const captionInputRef = useRef(null);
  const {
    suggestions: captionMentionSuggestions,
    mentionQuery: captionMentionQuery,
    insertMention: insertCaptionMention,
    handleInput: handleCaptionMentionInput,
    clearMention: clearCaptionMention,
    serializeForSubmit: serializeCaptionMentions,
    clearMentionRegistry: clearCaptionMentionRegistry,
  } = useMentionSearch(caption, setCaption, captionInputRef);

  const requiredProductCount = postType === "comparison" ? 2 : 1;
  const maxProducts = postType === "battle" ? 0 : requiredProductCount;

  const hasRequiredProducts = () => {
    if (postType === "battle") {
      return Boolean(battleSideAProduct && battleSideBProduct);
    }
    if (postType === "comparison") {
      return products.length >= 2;
    }
    return products.length >= 1;
  };

  // Search products (debounced on live input so the first character triggers a search)
  useEffect(() => {
    const query = productSearch.trim();
    if (query.length < 1) {
      setProductSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setShowSuggestions(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await api.get("/products/search", {
          params: { query, limit: 10 },
        });
        if (!cancelled) {
          setProductSuggestions(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error searching products:", err);
          setProductSuggestions([]);
        }
      } finally {
        if (!cancelled) {
          setSearching(false);
        }
      }
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [productSearch]);

  // Battle side A product search
  useEffect(() => {
    const query = battleSideASearch.trim();
    if (query.length < 1) {
      setBattleSideASuggestions([]);
      setBattleSideAShowSugg(false);
      return;
    }

    setBattleSideAShowSugg(true);
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .get("/products/search", { params: { query, limit: 8 } })
        .then((res) => {
          if (!cancelled) {
            setBattleSideASuggestions(Array.isArray(res.data) ? res.data : []);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setBattleSideASuggestions([]);
          }
        });
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [battleSideASearch]);

  // Battle side B product search
  useEffect(() => {
    const query = battleSideBSearch.trim();
    if (query.length < 1) {
      setBattleSideBSuggestions([]);
      setBattleSideBShowSugg(false);
      return;
    }

    setBattleSideBShowSugg(true);
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .get("/products/search", { params: { query, limit: 8 } })
        .then((res) => {
          if (!cancelled) {
            setBattleSideBSuggestions(Array.isArray(res.data) ? res.data : []);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setBattleSideBSuggestions([]);
          }
        });
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [battleSideBSearch]);

  const getProductId = (product) => {
    if (!product) return null;
    if (typeof product === "string") return product;
    return product._id || product.id || null;
  };

  const getTotalImageCount = (imagesMap) =>
    Object.values(imagesMap).reduce((sum, list) => sum + list.length, 0);

  const getProductNameById = (productId) => {
    const product = products.find((p) => getProductId(p) === productId);
    return product ? capitalizeFirst(product.name || "") : "";
  };

  const handlePostTypeChange = (nextType) => {
    if (nextType === postType) return;

    if (nextType === "comparison" && products.length > 2) {
      showToast({
        message:
          "Comparison posts support up to 2 products. Remove extra products first.",
        type: "info",
      });
    }

    // Clear battle-specific side state when leaving battle type
    if (postType === "battle" && nextType !== "battle") {
      setBattleSideAProduct(null);
      setBattleSideBProduct(null);
      setBattleSideASearch("");
      setBattleSideBSearch("");
      setProductImages((prev) => {
        const { "__sideA__": _a, "__sideB__": _b, ...rest } = prev;
        return rest;
      });
      setProductImagePreviews((prev) => {
        const { "__sideA__": _a, "__sideB__": _b, ...rest } = prev;
        return rest;
      });
    }

    setPostType(nextType);
  };

  // Handle image selection for a product
  const handleImageSelect = (productId, e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        showToast({
          message: `${file.name} is not an image file`,
          type: "error",
        });
        return false;
      }
      if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
        showToast({
          message: `${file.name} is too large (max 5MB)`,
          type: "error",
        });
        return false;
      }
      return true;
    });

    // For battle sides, limit is 5 per side; for regular posts, 5 total
    const isBattleSide = productId === "__sideA__" || productId === "__sideB__";
    const existingForKey = (productImages[productId] || []).length;
    const maxForKey = 5;
    const availableSlots = isBattleSide
      ? maxForKey - existingForKey
      : 5 - getTotalImageCount(productImages);

    if (availableSlots <= 0) {
      showToast({
        message: isBattleSide ? "Maximum 5 images per side" : "Maximum 5 images allowed per post",
        type: "error",
      });
      if (e.target) {
        e.target.value = "";
      }
      return;
    }

    const filesToAdd = validFiles.slice(0, availableSlots);
    if (filesToAdd.length < validFiles.length) {
      showToast({
        message: isBattleSide ? "Only 5 images allowed per side" : "Only 5 images allowed per post",
        type: "info",
      });
    }

    const newPreviews = filesToAdd.map((file) => URL.createObjectURL(file));
    setProductImages((prev) => ({
      ...prev,
      [productId]: [...(prev[productId] || []), ...filesToAdd],
    }));
    setProductImagePreviews((prev) => ({
      ...prev,
      [productId]: [...(prev[productId] || []), ...newPreviews],
    }));

    if (e.target) {
      e.target.value = "";
    }
  };

  // Remove image for a product
  const removeProductImage = (productId, index) => {
    setProductImages((prev) => ({
      ...prev,
      [productId]: (prev[productId] || []).filter((_, i) => i !== index),
    }));
    setProductImagePreviews((prev) => {
      const previews = prev[productId] || [];
      const removedPreview = previews[index];
      if (removedPreview && removedPreview.startsWith("blob:")) {
        URL.revokeObjectURL(removedPreview);
      }
      return {
        ...prev,
        [productId]: previews.filter((_, i) => i !== index),
      };
    });
  };

  // Handle general image selection (for posts without products)
  const handleGeneralImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        showToast({
          message: `${file.name} is not an image file`,
          type: "error",
        });
        return false;
      }
      if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
        showToast({
          message: `${file.name} is too large (max 5MB)`,
          type: "error",
        });
        return false;
      }
      return true;
    });

    const currentCount = generalImages.length;
    const availableSlots = 5 - currentCount;

    if (availableSlots <= 0) {
      showToast({
        message: "Maximum 5 images allowed per post",
        type: "error",
      });
      if (e.target) e.target.value = "";
      return;
    }

    const filesToAdd = validFiles.slice(0, availableSlots);
    if (filesToAdd.length < validFiles.length) {
      showToast({
        message: "Only 5 images allowed per post",
        type: "info",
      });
    }

    const newPreviews = filesToAdd.map((file) => URL.createObjectURL(file));

    setGeneralImages((prev) => [...prev, ...filesToAdd]);
    setGeneralImagePreviews((prev) => [...prev, ...newPreviews]);

    if (e.target) e.target.value = "";
  };

  const removeGeneralImage = (index) => {
    const preview = generalImagePreviews[index];
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }
    setGeneralImages((prev) => prev.filter((_, i) => i !== index));
    setGeneralImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Add product
  const addProduct = (product) => {
    if (products.length >= maxProducts) {
      showToast({
        message:
          postType === "comparison"
            ? "Comparison posts can have up to 2 products"
            : "Standard posts allow only 1 product",
        type: "error",
      });
      return;
    }

    if (products.some((p) => p._id === product._id)) {
      showToast({
        message: "Product already added",
        type: "error",
      });
      return;
    }

    const productId = getProductId(product);
    const isFirstProduct = products.length === 0;

    setProducts([...products, product]);
    if (productId) {
      setProductImages((prev) => ({
        ...prev,
        [productId]: prev[productId] || [],
      }));
      setProductImagePreviews((prev) => ({
        ...prev,
        [productId]: prev[productId] || [],
      }));
    }

    // Auto-fill title for Standard / Flex / Question when first product is tagged
    if (isFirstProduct && !title.trim() && product?.name) {
      if (postType !== "comparison" && postType !== "battle") {
        setTitle(capitalizeFirst(product.name));
      }
    }

    setProductSearch("");
    setShowSuggestions(false);
  };

  // Remove product
  const removeProduct = (productId) => {
    const previews = productImagePreviews[productId] || [];
    previews.forEach((preview) => {
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }
    });
    setProducts(products.filter((p) => p._id !== productId));
    setProductImages((prev) => {
      const { [productId]: _, ...rest } = prev;
      return rest;
    });
    setProductImagePreviews((prev) => {
      const { [productId]: _, ...rest } = prev;
      return rest;
    });
  };

  useEffect(() => {
    const productIds = products.map((p) => getProductId(p)).filter(Boolean);
    if (postType !== "comparison") {
      return;
    }

    setComparisonOptionAProductId((prev) => {
      if (prev && productIds.includes(prev)) return prev;
      return productIds[0] || "";
    });

    setComparisonOptionBProductId((prev) => {
      if (prev && productIds.includes(prev) && prev !== comparisonOptionAProductId)
        return prev;
      return productIds.find((id) => id !== (productIds[0] || "")) || "";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, postType]);

  useEffect(() => {
    if (postType !== "comparison" && postType !== "battle") {
      return;
    }
    if (!comparisonOptionATitle && comparisonOptionAProductId) {
      setComparisonOptionATitle(getProductNameById(comparisonOptionAProductId));
    }
    if (!comparisonOptionBTitle && comparisonOptionBProductId) {
      setComparisonOptionBTitle(getProductNameById(comparisonOptionBProductId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonOptionAProductId, comparisonOptionBProductId, postType]);

  // Submit post
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate products are tagged
    if (postType === "battle") {
      if (!battleSideAProduct || !battleSideBProduct) {
        showToast({ message: "Please link a product for both sides of the Battle", type: "error" });
        return;
      }
    } else if (postType === "comparison") {
      if (products.length < 2) {
        showToast({ message: "Please tag 2 products for the comparison", type: "error" });
        return;
      }
    } else if (products.length < 1) {
      showToast({ message: "Please tag a product", type: "error" });
      return;
    }

    // Validate battle sides
    if (postType === "battle") {
      if (!comparisonOptionATitle.trim() || !comparisonOptionBTitle.trim()) {
        showToast({ message: "Please name both sides of the Battle", type: "error" });
        return;
      }
      const sideAKey = battleSideAProduct?._id || "__sideA__";
      const sideBKey = battleSideBProduct?._id || "__sideB__";
      const sideAImages = productImages[sideAKey] || [];
      const sideBImages = productImages[sideBKey] || [];
      if (sideAImages.length === 0 || sideBImages.length === 0) {
        showToast({ message: "Please upload at least one image for each side", type: "error" });
        return;
      }
    }

    // Validate comparison
    if (postType === "comparison") {
      if (!comparisonOptionATitle.trim() || !comparisonOptionBTitle.trim()) {
        showToast({ message: "Please provide titles for both comparison options", type: "error" });
        return;
      }
    }

    const productIds = postType === "battle"
      ? [battleSideAProduct, battleSideBProduct].filter(Boolean).map((p) => p._id || p.id)
      : products.map((p) => (typeof p === "string" ? p : p._id || p.id)).filter(Boolean);

    if (postType !== "battle") {
      const missingImages = productIds.filter(
        (productId) => (productImages[productId] || []).length === 0
      );
      if (missingImages.length > 0) {
        showToast({ message: "Please add at least one image for each product", type: "error" });
        return;
      }
    }

    const productImageCount = getTotalImageCount(productImages);
    const generalImageCount = generalImages.length;
    const totalImages = productImageCount + generalImageCount;

    if (totalImages === 0) {
      showToast({ message: "Please upload at least one image", type: "error" });
      return;
    }

    if (totalImages > 10) {
      showToast({ message: "Maximum 5 images per side (10 total) allowed", type: "error" });
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      const productImagesOrder = [];

      if (postType === "battle") {
        // Battle: upload side A images then side B images, track order
        const sideAKey = battleSideAProduct?._id || "__sideA__";
        const sideBKey = battleSideBProduct?._id || "__sideB__";
        const sideAFiles = productImages[sideAKey] || [];
        const sideBFiles = productImages[sideBKey] || [];
        sideAFiles.forEach((img) => formData.append("images", img));
        sideBFiles.forEach((img) => formData.append("images", img));
        productImagesOrder.push(
          { productId: battleSideAProduct?._id || null, count: sideAFiles.length, side: "A" },
          { productId: battleSideBProduct?._id || null, count: sideBFiles.length, side: "B" }
        );
      } else if (productIds.length > 0) {
        productIds.forEach((productId) => {
          const files = productImages[productId] || [];
          productImagesOrder.push({ productId, count: files.length });
          files.forEach((img) => formData.append("images", img));
        });
      } else {
        generalImages.forEach((img) => formData.append("images", img));
      }

      const postTitle = postType === "battle"
        ? `${comparisonOptionATitle.trim()} vs ${comparisonOptionBTitle.trim()}`
        : title || "";

      formData.append("products", JSON.stringify(productIds));
      formData.append("productImagesOrder", JSON.stringify(productImagesOrder));
      formData.append("postType", postType);
      formData.append("title", postTitle);
      formData.append("caption", serializeCaptionMentions(caption || ""));
      formData.append("visibility", visibility || "public");

      if (postType === "comparison" || postType === "battle") {
        formData.append(
          "comparison",
          JSON.stringify({
            optionA: {
              title: comparisonOptionATitle.trim(),
              productId: postType === "battle" ? (battleSideAProduct?._id || null) : (comparisonOptionAProductId || null),
            },
            optionB: {
              title: comparisonOptionBTitle.trim(),
              productId: postType === "battle" ? (battleSideBProduct?._id || null) : (comparisonOptionBProductId || null),
            },
          }),
        );
      }

      if (postType === "battle" && battleDuration) {
        const endsAt = new Date(Date.now() + parseInt(battleDuration, 10) * 60 * 60 * 1000);
        formData.append("endsAt", endsAt.toISOString());
      }

      if (tags) {
        const tagArray = tags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t.length > 0);
        formData.append("tags", JSON.stringify(tagArray));
      }

      if (location) {
        formData.append("location", location);
      }

      // Don't set Content-Type - let axios/browser set it automatically with boundary for FormData
      const res = await api.post("/posts", formData);

      showToast({
        message: "Post created successfully!",
        type: "success",
      });

      // Reset form
      resetForm();
      onPostCreated?.(res.data);
      onClose();
    } catch (err) {
      console.error("Error creating post:", err.response?.data?.message || err.message);
      showToast({
        message:
          err.response?.data?.message || err.message || "Failed to create post",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    // Revoke general image previews
    generalImagePreviews.forEach((url) => {
      if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
    });
    setGeneralImages([]);
    setGeneralImagePreviews([]);

    setProductImages({});
    setProductImagePreviews({});
    setProducts([]);
    setProductSearch("");
    setTitle("");
    setCaption("");
    clearCaptionMentionRegistry();
    setTags("");
    setLocation("");
    setVisibility("public");
    setPostType(initialPostType || "standard");
    setComparisonOptionAProductId("");
    setComparisonOptionBProductId("");
    setComparisonOptionATitle("");
    setComparisonOptionBTitle("");
    setBattleDuration("24");
    setBattleSideASearch("");
    setBattleSideBSearch("");
    setBattleSideASuggestions([]);
    setBattleSideBSuggestions([]);
    setBattleSideAProduct(null);
    setBattleSideBProduct(null);
    setStep(1);
  };

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      Object.values(productImagePreviews).forEach((previews) => {
        previews.forEach((url) => {
          if (url && url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
      });
    };
  }, [productImagePreviews]);

  if (!isOpen) return null;

  return (
    <Modal
      onClose={onClose}
      className={styles.modalWrapper}
      hideDefaultClose={true}
      fullScreenOnMobile
      ariaLabel={
        postType === "flex"
          ? "Flex Post"
          : postType === "battle"
            ? "Battle"
            : postType === "question"
              ? "Ask a Question"
              : "Create Post"
      }
    >
      <div className={styles.createPostModal}>
        <div className={styles.modalHeader}>
          <h2>
            {postType === "flex" ? "🔥 Flex Post" : postType === "battle" ? "⚡ Battle" : postType === "question" ? "❓ Ask a Question" : "Create Post"}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close dialog"
          >
            <FaTimes aria-hidden="true" />
          </button>
        </div>

        {postType === "flex" && (
          <div className={styles.flexBanner}>
            <FaFire className={styles.flexBannerIcon} />
            <div>
              <strong>Flex Mode</strong>
              <p>Show off something you own. Tag the product and let the community react.</p>
            </div>
          </div>
        )}

        {postType === "battle" && (
          <div className={styles.battleBanner}>
            <FaBolt className={styles.battleBannerIcon} />
            <div>
              <strong>Battle Mode</strong>
              <p>Tag 2 products. Ask the community which one wins. Votes are hidden until after you vote.</p>
            </div>
          </div>
        )}

        {postType === "question" && (
          <div className={styles.questionBanner}>
            <FaQuestionCircle className={styles.questionBannerIcon} />
            <div>
              <strong>Ask a Question</strong>
              <p>Post your question to the community. Replies become the answers.</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.section}>
            <label className={styles.sectionLabel}>Post Type</label>
            <div className={styles.visibilityOptions}>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="postType"
                  value="standard"
                  checked={postType === "standard"}
                  onChange={(e) => handlePostTypeChange(e.target.value)}
                />
                <span>Standard</span>
              </label>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="postType"
                  value="battle"
                  checked={postType === "battle"}
                  onChange={(e) => handlePostTypeChange(e.target.value)}
                />
                <span>⚡ Battle</span>
              </label>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="postType"
                  value="flex"
                  checked={postType === "flex"}
                  onChange={(e) => handlePostTypeChange(e.target.value)}
                />
                <span>🔥 Flex</span>
              </label>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="postType"
                  value="question"
                  checked={postType === "question"}
                  onChange={(e) => handlePostTypeChange(e.target.value)}
                />
                <span>❓ Question</span>
              </label>
            </div>
          </div>

          {/* Step 1: Products — hidden for Battle (each side has its own product search) */}
          {postType !== "battle" && <div className={styles.section}>
            <label className={styles.sectionLabel}>
              <FaTag aria-hidden="true" /> Tag Product{postType === "comparison" ? "s (2 required)" : " *"}
            </label>
            <div className={styles.productSearchRow}>
              <div className={styles.productSearchWrapper}>
              <div className={styles.searchInputWrapper}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search for products..."
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  className={styles.searchInput}
                />
                {searching && (
                  <span className={styles.loading}>Searching...</span>
                )}
              </div>

              {showSuggestions && productSearch.trim().length > 0 && (
                <div className={styles.suggestionsList}>
                  {searching && productSuggestions.length === 0 && (
                    <div className={styles.suggestionItem}>Searching...</div>
                  )}
                  {productSuggestions.map((product) => (
                    <div
                      key={product._id}
                      className={styles.suggestionItem}
                      onClick={() => addProduct(product)}
                    >
                      <ProductSuggestionThumb product={product} />
                      <div className={styles.suggestionInfo}>
                        <div className={styles.suggestionName}>
                          {capitalizeFirst(product.name)}
                        </div>
                        {product.brand && (
                          <div className={styles.suggestionBrand}>
                            {typeof product.brand === "object"
                              ? product.brand.name
                              : product.brand}
                          </div>
                        )}
                        {product.price && (
                          <div className={styles.suggestionPrice}>
                            ${product.price}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>

            {/* Selected Products */}
            {products.length > 0 && (
              <div className={styles.selectedProducts}>
                <h4>
                  Selected Products ({products.length}/
                  {maxProducts})
                </h4>
                {products.map((product) => (
                  <div key={product._id} className={styles.selectedProductCard}>
                    <div className={styles.productInfo}>
                      {product.images && product.images.length > 0 && (
                        <img
                          src={getImageUrl(product.images[0])}
                          alt={product.name}
                          className={styles.productImage}
                        />
                      )}
                      <div className={styles.productDetails}>
                        <div className={styles.productName}>{product.name}</div>
                        {product.brand && (
                          <div className={styles.productBrand}>
                            {typeof product.brand === "object"
                              ? product.brand.name
                              : product.brand}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles.removeProductButton}
                      onClick={() => removeProduct(product._id)}
                      aria-label="Remove selected product"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {products.length === 0 && (
              <p className={styles.helpText}>
                {postType === "comparison"
                  ? "Search and add 2 products from the catalog."
                  : "Search and select a product from the catalog to continue."}
              </p>
            )}
          </div>}

          {postType === "battle" && (
            <div className={styles.section}>
              <label className={styles.sectionLabel}><FaBolt /> Battle Setup *</label>
              <p className={styles.helpText}>
                Give each side a name, link a product from the catalog, and upload photos for that side.
              </p>
              <div className={styles.battleOptionsGrid}>
                {/* ---- Side A ---- */}
                <div className={styles.battleSideCard}>
                  <div className={styles.battleSideHeading}>⚔️ Side A</div>
                  <input
                    type="text"
                    value={comparisonOptionATitle}
                    onChange={(e) => setComparisonOptionATitle(e.target.value)}
                    placeholder="e.g. iPhone 16 Pro"
                    className={styles.searchInput}
                    required
                  />
                  <label className={styles.helpText}>Link a product</label>
                  <div className={styles.productSearchWrapper}>
                    {battleSideAProduct ? (
                      <div className={styles.linkedProductBadge}>
                        <span>{capitalizeFirst(battleSideAProduct.name)}</span>
                        <button
                          type="button"
                          className={styles.removeBadge}
                          onClick={() => { setBattleSideAProduct(null); setBattleSideASearch(""); }}
                        ><FaTimes /></button>
                      </div>
                    ) : (
                      <div className={styles.searchInputWrapper}>
                        <FaSearch className={styles.searchIcon} />
                        <input
                          type="text"
                          value={battleSideASearch}
                          onChange={(e) => { setBattleSideASearch(e.target.value); setBattleSideAShowSugg(true); }}
                          onFocus={() => setBattleSideAShowSugg(true)}
                          placeholder="Search for a product…"
                          className={styles.searchInput}
                          aria-required="true"
                        />
                      </div>
                    )}
                    {battleSideAShowSugg && battleSideASuggestions.length > 0 && (
                      <div className={styles.suggestionsList}>
                        {battleSideASuggestions.map((p) => (
                          <div
                            key={p._id}
                            className={styles.suggestionItem}
                            onClick={() => {
                              setBattleSideAProduct(p);
                              if (!comparisonOptionATitle.trim()) setComparisonOptionATitle(capitalizeFirst(p.name));
                              setBattleSideASearch("");
                              setBattleSideASuggestions([]);
                              setBattleSideAShowSugg(false);
                            }}
                          >
                            <ProductSuggestionThumb product={p} />
                            <div className={styles.suggestionInfo}>
                              <div className={styles.suggestionName}>
                                {capitalizeFirst(p.name)}
                              </div>
                              {p.brand && (
                                <div className={styles.suggestionBrand}>
                                  {typeof p.brand === "object" ? p.brand.name : p.brand}
                                </div>
                              )}
                              {p.price && (
                                <div className={styles.suggestionPrice}>${p.price}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Image upload for Side A */}
                  {(() => {
                    const key = battleSideAProduct?._id || "__sideA__";
                    const previews = productImagePreviews[key] || [];
                    return (
                      <div className={styles.sideImageUpload}>
                        <span className={styles.helpText}>Photos ({previews.length}/5)</span>
                        <div className={styles.imageGrid}>
                          {previews.map((src, i) => (
                            <div key={i} className={styles.imagePreview}>
                              <img src={src} alt={`Side A ${i + 1}`} />
                              <button type="button" className={styles.removeImageButton} onClick={() => removeProductImage(key, i)}><FaTimes /></button>
                            </div>
                          ))}
                          {previews.length < 5 && (
                            <label className={previews.length === 0 ? styles.uploadButton : styles.addImageButton}>
                              <FaImage />
                              {previews.length === 0 && <span>Upload Photos</span>}
                              <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" multiple onChange={(e) => handleImageSelect(key, e)} className={styles.hiddenInput} />
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className={styles.battleVsLabel}>VS</div>

                {/* ---- Side B ---- */}
                <div className={styles.battleSideCard}>
                  <div className={styles.battleSideHeading}>⚔️ Side B</div>
                  <input
                    type="text"
                    value={comparisonOptionBTitle}
                    onChange={(e) => setComparisonOptionBTitle(e.target.value)}
                    placeholder="e.g. Galaxy S25"
                    className={styles.searchInput}
                    required
                  />
                  <label className={styles.helpText}>Link a product</label>
                  <div className={styles.productSearchWrapper}>
                    {battleSideBProduct ? (
                      <div className={styles.linkedProductBadge}>
                        <span>{capitalizeFirst(battleSideBProduct.name)}</span>
                        <button
                          type="button"
                          className={styles.removeBadge}
                          onClick={() => { setBattleSideBProduct(null); setBattleSideBSearch(""); }}
                        ><FaTimes /></button>
                      </div>
                    ) : (
                      <div className={styles.searchInputWrapper}>
                        <FaSearch className={styles.searchIcon} />
                        <input
                          type="text"
                          value={battleSideBSearch}
                          onChange={(e) => { setBattleSideBSearch(e.target.value); setBattleSideBShowSugg(true); }}
                          onFocus={() => setBattleSideBShowSugg(true)}
                          placeholder="Search for a product…"
                          className={styles.searchInput}
                          aria-required="true"
                        />
                      </div>
                    )}
                    {battleSideBShowSugg && battleSideBSuggestions.length > 0 && (
                      <div className={styles.suggestionsList}>
                        {battleSideBSuggestions.map((p) => (
                          <div
                            key={p._id}
                            className={styles.suggestionItem}
                            onClick={() => {
                              setBattleSideBProduct(p);
                              if (!comparisonOptionBTitle.trim()) setComparisonOptionBTitle(capitalizeFirst(p.name));
                              setBattleSideBSearch("");
                              setBattleSideBSuggestions([]);
                              setBattleSideBShowSugg(false);
                            }}
                          >
                            <ProductSuggestionThumb product={p} />
                            <div className={styles.suggestionInfo}>
                              <div className={styles.suggestionName}>
                                {capitalizeFirst(p.name)}
                              </div>
                              {p.brand && (
                                <div className={styles.suggestionBrand}>
                                  {typeof p.brand === "object" ? p.brand.name : p.brand}
                                </div>
                              )}
                              {p.price && (
                                <div className={styles.suggestionPrice}>${p.price}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {(() => {
                    const key = battleSideBProduct?._id || "__sideB__";
                    const previews = productImagePreviews[key] || [];
                    return (
                      <div className={styles.sideImageUpload}>
                        <span className={styles.helpText}>Photos ({previews.length}/5)</span>
                        <div className={styles.imageGrid}>
                          {previews.map((src, i) => (
                            <div key={i} className={styles.imagePreview}>
                              <img src={src} alt={`Side B ${i + 1}`} />
                              <button type="button" className={styles.removeImageButton} onClick={() => removeProductImage(key, i)}><FaTimes /></button>
                            </div>
                          ))}
                          {previews.length < 5 && (
                            <label className={previews.length === 0 ? styles.uploadButton : styles.addImageButton}>
                              <FaImage />
                              {previews.length === 0 && <span>Upload Photos</span>}
                              <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" multiple onChange={(e) => handleImageSelect(key, e)} className={styles.hiddenInput} />
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <label className={styles.helpText} style={{ marginTop: 16 }}>Battle Duration</label>
              <select
                value={battleDuration}
                onChange={(e) => setBattleDuration(e.target.value)}
                className={styles.searchInput}
                style={{ maxWidth: 200 }}
              >
                <option value="24">24 hours</option>
                <option value="48">48 hours</option>
                <option value="72">3 days</option>
                <option value="168">1 week</option>
              </select>
            </div>
          )}

          {postType === "comparison" && (
            <div className={styles.section}>
              <label className={styles.sectionLabel}>Comparison Setup *</label>
              <p className={styles.helpText}>
                Compare two products (from tagged list or custom names). Users vote on which wins each category.
              </p>
              <div className={styles.section}>
                <label className={styles.helpText}>Option A</label>
                <select
                  value={comparisonOptionAProductId}
                  onChange={(e) => setComparisonOptionAProductId(e.target.value)}
                  className={styles.searchInput}
                >
                  <option value="">Custom / Other…</option>
                  {products.map((product) => {
                    const id = getProductId(product);
                    return (
                      <option key={`a-${id}`} value={id}>
                        {capitalizeFirst(product.name)}
                      </option>
                    );
                  })}
                </select>
                <input
                  type="text"
                  value={comparisonOptionATitle}
                  onChange={(e) => setComparisonOptionATitle(e.target.value)}
                  placeholder="Name or label (e.g. iPhone 16 Pro)"
                  className={styles.searchInput}
                  required
                />
              </div>
              <div className={styles.section}>
                <label className={styles.helpText}>Option B</label>
                <select
                  value={comparisonOptionBProductId}
                  onChange={(e) => setComparisonOptionBProductId(e.target.value)}
                  className={styles.searchInput}
                >
                  <option value="">Custom / Other…</option>
                  {products.map((product) => {
                    const id = getProductId(product);
                    return (
                      <option key={`b-${id}`} value={id}>
                        {capitalizeFirst(product.name)}
                      </option>
                    );
                  })}
                </select>
                <input
                  type="text"
                  value={comparisonOptionBTitle}
                  onChange={(e) => setComparisonOptionBTitle(e.target.value)}
                  placeholder="Name or label (e.g. Galaxy S25)"
                  className={styles.searchInput}
                  required
                />
              </div>
            </div>
          )}

          {/* Title field — hidden for Battle (auto-filled as "A vs B") */}
          {postType !== "battle" && (
            <div className={styles.section}>
              <label className={styles.sectionLabel}>
                {postType === "flex" ? "What are you flexing?" : "Name of product"}
              </label>
              <input
                type="text"
                value={title}
                maxLength={120}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={postType === "flex" ? "Name the item you're showing off…" : "Give your post a title..."}
                className={styles.captionInput}
              />
            </div>
          )}

          <div className={styles.section}>
            <label className={styles.sectionLabel}>
              {postType === "flex" ? "Why are you flexing? 🔥" : "Caption"}
            </label>
            <div style={{ position: "relative" }}>
              <textarea
                ref={captionInputRef}
                value={caption}
                required
                maxLength={500}
                onChange={(e) => {
                  setCaption(e.target.value);
                  handleCaptionMentionInput(e);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") clearCaptionMention();
                }}
                placeholder={
                  postType === "flex"
                    ? "Tell the world why this is so fire. Brag about it!"
                    : "Tell us about this post… type @ to tag a product"
                }
                className={`${styles.captionInput} ${postType === "flex" ? styles.flexCaptionInput : ""}`}
                rows={4}
              />
              {captionMentionQuery !== null && (
                <MentionDropdown
                  suggestions={captionMentionSuggestions}
                  query={captionMentionQuery}
                  onSelect={(product) => insertCaptionMention(product)}
                />
              )}
            </div>
            <div style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)", textAlign: "right", marginTop: "4px" }}>
              {caption.length}/500
            </div>
          </div>

          {/* Images section — hidden for Battle (images handled per-side in Battle Setup above) */}
          {postType !== "battle" && <div className={styles.section}>
            <label className={styles.sectionLabel}>
              <FaImage /> Product Images (1-5 total) *
            </label>

            {products.length > 0 ? (
              // Per-product image upload when products are selected
              <div className={styles.productImageList}>
                {products.map((product) => {
                  const productId = getProductId(product);
                  const previews = productImagePreviews[productId] || [];
                  const totalImages = getTotalImageCount(productImages);
                  return (
                    <div key={productId} className={styles.productImageSection}>
                      <div className={styles.productImageHeader}>
                        <div className={styles.productImageTitle}>
                          {product.name}
                        </div>
                        <div className={styles.productImageCount}>
                          {previews.length} image
                          {previews.length !== 1 ? "s" : ""}
                        </div>
                      </div>
                      <div className={styles.imageUploadArea}>
                        {previews.length > 0 ? (
                          <div className={styles.imageGrid}>
                            {previews.map((preview, index) => (
                              <div key={index} className={styles.imagePreview}>
                                <img
                                  src={preview}
                                  alt={`Preview ${index + 1}`}
                                />
                                <button
                                  type="button"
                                  className={styles.removeImageButton}
                                  onClick={() =>
                                    removeProductImage(productId, index)
                                  }
                                  aria-label="Remove selected image"
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            ))}
                            {totalImages < 5 && (
                              <label className={styles.addImageButton}>
                                <FaImage />
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/jpg,image/webp"
                                  multiple
                                  onChange={(e) =>
                                    handleImageSelect(productId, e)
                                  }
                                  className={styles.hiddenInput}
                                />
                              </label>
                            )}
                          </div>
                        ) : (
                          <label className={styles.uploadButton}>
                            <FaImage />
                            <span>Upload Image</span>
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/webp"
                              multiple
                              onChange={(e) => handleImageSelect(productId, e)}
                              className={styles.hiddenInput}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className={styles.helpText}>
                Tag a product above to upload images.
              </p>
            )}

            {products.length > 0 && (
              <p className={styles.helpText}>
                Add at least one image per product. Max 5 images total.
              </p>
            )}
          </div>}

          <div className={styles.section}>
            <label className={styles.sectionLabel}>Visibility</label>
            <div className={styles.visibilityOptions}>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={visibility === "public"}
                  onChange={(e) => setVisibility(e.target.value)}
                />
                <FaGlobe />
                <span>Public</span>
              </label>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="visibility"
                  value="followers"
                  checked={visibility === "followers"}
                  onChange={(e) => setVisibility(e.target.value)}
                />
                <FaUserFriends />
                <span>Followers</span>
              </label>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={visibility === "private"}
                  onChange={(e) => setVisibility(e.target.value)}
                />
                <FaLock />
                <span>Private</span>
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={(() => {
                if (loading) return true;
                if (!hasRequiredProducts()) return true;
                if (postType === "battle") {
                  const sideAKey = battleSideAProduct?._id || "__sideA__";
                  const sideBKey = battleSideBProduct?._id || "__sideB__";
                  return (
                    !comparisonOptionATitle.trim() ||
                    !comparisonOptionBTitle.trim() ||
                    (productImages[sideAKey] || []).length === 0 ||
                    (productImages[sideBKey] || []).length === 0
                  );
                }
                if (postType === "comparison") {
                  return (
                    !comparisonOptionAProductId ||
                    !comparisonOptionBProductId ||
                    !comparisonOptionATitle.trim() ||
                    !comparisonOptionBTitle.trim() ||
                    getTotalImageCount(productImages) === 0
                  );
                }
                return getTotalImageCount(productImages) === 0;
              })()}
            >
              {loading ? "Posting..." : "Create Post"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CreatePostModal;
